import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { AccountService } from '../../services/account.service';
import { AuthService } from '../../services/auth.service';
import { IUser } from '../../models/user.model';
import { RegisterValidators } from '../../validators/register-validators';
import { ErrorMessageUtils } from '../../utils/ErrorMessageUtils';
import { ConfirmService } from '../../ui/overlay/confirm.service';
import { AlertSeverity } from '../../ui/alert.component';

/** The word a member has to type to confirm erasure. Compared case-sensitively after a
 *  trim: mobile keyboards autocapitalise and append spaces. */
const DELETE_CONFIRMATION = 'VERWIJDER';

interface Alert {
  show: boolean;
  severity: AlertSeverity;
  message: string;
}

@Component({
  selector: 'app-my-data',
  templateUrl: './my-data.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MyDataComponent {
  readonly deleteConfirmation = DELETE_CONFIRMATION;

  user$: Observable<IUser | null> = this.authService.user$;

  newEmail = new FormControl('', [Validators.required, Validators.email]);
  emailPassword = new FormControl('', [Validators.required]);
  emailForm = new FormGroup({
    newEmail: this.newEmail,
    emailPassword: this.emailPassword,
  });

  currentPassword = new FormControl('', [Validators.required]);
  // minLength sits on the first control on purpose: RegisterValidators.match calls
  // setErrors on the second one and would wipe any error it carries itself.
  newPassword = new FormControl('', [
    Validators.required,
    Validators.minLength(6),
  ]);
  confirmPassword = new FormControl('', [Validators.required]);
  passwordForm = new FormGroup(
    {
      currentPassword: this.currentPassword,
      newPassword: this.newPassword,
      confirmPassword: this.confirmPassword,
    },
    [RegisterValidators.match('newPassword', 'confirmPassword')],
  );

  deletePassword = new FormControl('', [Validators.required]);
  deleteConfirm = new FormControl('', [Validators.required]);
  deleteForm = new FormGroup({
    deletePassword: this.deletePassword,
    deleteConfirm: this.deleteConfirm,
  });

  emailSubmission = false;
  passwordSubmission = false;
  deleteSubmission = false;

  emailAlert: Alert = { show: false, severity: 'info', message: '' };
  passwordAlert: Alert = { show: false, severity: 'info', message: '' };
  deleteAlert: Alert = { show: false, severity: 'info', message: '' };

  constructor(
    private accountService: AccountService,
    private authService: AuthService,
    private confirm: ConfirmService,
    private router: Router,
  ) {}

  get deleteWordTyped(): boolean {
    return (this.deleteConfirm.value ?? '').trim() === DELETE_CONFIRMATION;
  }

  async changeEmail(): Promise<void> {
    if (this.emailForm.invalid || this.emailSubmission) {
      return;
    }
    this.emailSubmission = true;
    try {
      // Lowercased to match how registration stores it. No trim: Validators.email
      // already rejects anything with whitespace, so the button never enables.
      await this.accountService.changeEmail(
        (this.newEmail.value ?? '').toLowerCase(),
        this.emailPassword.value ?? '',
      );
      this.emailAlert = {
        show: true,
        severity: 'success',
        message:
          'We stuurden een bevestigingslink naar je nieuwe e-mailadres. Klik die link, daarna log je in met het nieuwe adres.',
      };
      this.emailForm.reset();
    } catch (error) {
      this.emailAlert = {
        show: true,
        severity: 'error',
        message: ErrorMessageUtils.determineFrontendMessageBasedOnError(error),
      };
    } finally {
      this.emailSubmission = false;
    }
  }

  async changePassword(): Promise<void> {
    if (this.passwordForm.invalid || this.passwordSubmission) {
      return;
    }
    this.passwordSubmission = true;
    try {
      await this.accountService.changePassword(
        this.currentPassword.value ?? '',
        this.newPassword.value ?? '',
      );
      this.passwordAlert = {
        show: true,
        severity: 'success',
        message: 'Je wachtwoord is aangepast.',
      };
      this.passwordForm.reset();
    } catch (error) {
      this.passwordAlert = {
        show: true,
        severity: 'error',
        message: ErrorMessageUtils.determineFrontendMessageBasedOnError(error),
      };
    } finally {
      this.passwordSubmission = false;
    }
  }

  async confirmDelete(): Promise<void> {
    if (
      this.deleteForm.invalid ||
      !this.deleteWordTyped ||
      this.deleteSubmission
    ) {
      return;
    }
    const confirmed = await this.confirm.ask({
      message:
        'Je account, je chipkoppeling en al je reservaties worden definitief verwijderd. Dit kan niet ongedaan gemaakt worden.',
      acceptLabel: 'Ja, verwijder',
    });
    if (confirmed) {
      await this.deleteAccount();
    }
  }

  private async deleteAccount(): Promise<void> {
    this.deleteSubmission = true;
    try {
      await this.accountService.deleteAccount(this.deletePassword.value ?? '');
      localStorage.clear();
      await this.router.navigate([
        '/login',
        { showAccountDeletedMessage: true },
      ]);
    } catch (error) {
      this.deleteAlert = {
        show: true,
        severity: 'error',
        message: ErrorMessageUtils.determineFrontendMessageBasedOnError(error),
      };
    } finally {
      this.deleteSubmission = false;
    }
  }
}
